import { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors } from "./payments.styles";
import ReportLoader from "../../Reports/Loader";
import PaymentMetrics from "./PaymentMetrics";
import RevenueTrend from "./RevenueTrend";
import RevenueByService from "./RevenueByService";
import TransactionHistory from "./TransactionHistory";
import { getPaymentsReportApi } from "../../../api/reports.api";

const Payments = ({ dateRange }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const [metricsData, setMetricsData] = useState(null);
  const [revenueTrendData, setRevenueTrendData] = useState([]);
  const [revenueByServiceData, setRevenueByServiceData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [filters, setFilters] = useState({});

  // Convert dateRange to stable strings for dependency tracking
  const dateRangeKey = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    }
    return "";
  }, [dateRange]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const fetchPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const defaultFrom = new Date("2020-01-01");
      const defaultTo = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

      const fromDate = filters.startDate
        ? new Date(filters.startDate)
        : dateRange?.startDate || defaultFrom;
      const toDate = filters.endDate
        ? new Date(filters.endDate)
        : dateRange?.endDate || defaultTo;

      const params = {
        from: fromDate instanceof Date ? fromDate.toISOString() : new Date(fromDate).toISOString(),
        to: toDate instanceof Date ? toDate.toISOString() : new Date(toDate).toISOString(),
      };

      
      const response = await getPaymentsReportApi(params);
      const { items = [] } = response.data?.data || {};

      // Filter items to only include those with payments in the selected date range
      const filteredItems = items.filter(item => {
        // Check if item has paymentsInRange (backend-filtered payments)
        if (item.paymentsInRange && item.paymentsInRange.length > 0) {
          return true;
        }
        // Fallback: check if any payment falls within the date range
        if (dateRange?.startDate && dateRange?.endDate && item.payments?.length > 0) {
          const startDate = new Date(dateRange.startDate);
          const endDate = new Date(dateRange.endDate);
          // Set end date to end of day
          endDate.setHours(23, 59, 59, 999);
          return item.payments.some(p => {
            const paymentDate = new Date(p.created_at);
            return paymentDate >= startDate && paymentDate <= endDate;
          });
        }
        return false;
      });

      const transformedTransactions = filteredItems.map((item, index) => {

        // Use paymentsInRange if available, otherwise fall back to all payments
        const relevantPayments = item.paymentsInRange && item.paymentsInRange.length > 0
          ? item.paymentsInRange
          : item.payments || [];

        const latestPayment = relevantPayments.length > 0
          ? relevantPayments[relevantPayments.length - 1]
          : null;


        const paymentDate = latestPayment?.created_at || item.created_at;


        const date = paymentDate
          ? new Date(paymentDate).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })
          : "-";


        const serviceType = Array.isArray(item.services)
          ? item.services[0]
          : item.services || "";

        const totalPaid = relevantPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 0;

        const orderCost = item.total || item.custom_fields?.order_cost || 0;


        const pendingAmount = Math.max(0, orderCost - totalPaid);


        let paymentStatus = "pending";
        if (orderCost > 0) {
          if (totalPaid >= orderCost) {
            paymentStatus = "success";
          } else if (totalPaid > 0) {
            paymentStatus = "pending";
          } else {
            paymentStatus = "failed";
          }
        }

        return {
          id: item._id || index + 1,
          date,
          rawDate: paymentDate ? new Date(paymentDate) : new Date(0),
          clientName: item.client_name || "Unknown",
          serviceType: serviceType?.toLowerCase() || "",
          orderCost: orderCost,
          paid: totalPaid,
          pending: pendingAmount,
          method: latestPayment?.mode || "-",
          status: paymentStatus,
        };
      });


      transformedTransactions.sort((a, b) => b.rawDate - a.rawDate);

      setTransactionData(transformedTransactions);


      const totalRevenue = transformedTransactions.reduce((sum, t) => sum + t.paid, 0);
      const pendingTransactions = transformedTransactions.filter(t => t.status === "pending" || t.status === "failed");
      const totalPending = pendingTransactions.reduce((sum, t) => sum + t.pending, 0);
      const pendingCount = pendingTransactions.length;
      const avgDealValue = transformedTransactions.length > 0 ? Math.round(totalRevenue / transformedTransactions.length) : 0;

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      let currentMonthRevenue = 0;
      let lastMonthRevenue = 0;

      // Only include payments within the selected date range
      filteredItems.forEach(item => {
        const relevantPayments = item.paymentsInRange && item.paymentsInRange.length > 0
          ? item.paymentsInRange
          : (dateRange?.startDate && dateRange?.endDate
              ? (item.payments || []).filter(p => {
                  const paymentDate = new Date(p.created_at);
                  const startDate = new Date(dateRange.startDate);
                  const endDate = new Date(dateRange.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  return paymentDate >= startDate && paymentDate <= endDate;
                })
              : (item.payments || []));

        relevantPayments.forEach(payment => {
          if (payment.created_at && payment.amount) {
            const paymentDate = new Date(payment.created_at);
            const paymentMonth = paymentDate.getMonth();
            const paymentYear = paymentDate.getFullYear();

            if (paymentMonth === currentMonth && paymentYear === currentYear) {
              currentMonthRevenue += Number(payment.amount) || 0;
            } else if (paymentMonth === lastMonth && paymentYear === lastMonthYear) {
              lastMonthRevenue += Number(payment.amount) || 0;
            }
          }
        });
      });

      let monthlyGrowth = "-";
      if (lastMonthRevenue > 0) {
        const growthPercent = Math.round(((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100);
        monthlyGrowth = growthPercent >= 0 ? `+${growthPercent}%` : `${growthPercent}%`;
      } else if (currentMonthRevenue > 0) {
        monthlyGrowth = "+100%"; 
      }

      const totalOrderCost = transformedTransactions.reduce((sum, t) => sum + t.orderCost, 0);
      const avgPaidPerLead = transformedTransactions.length > 0 ? Math.round(totalRevenue / transformedTransactions.length) : 0;

      const metricsDataValue = {
        totalRevenue: formatCurrency(totalOrderCost),
        revenueTrend: `${transformedTransactions.length} total orders`,
        pendingPayments: formatCurrency(totalPending),
        pendingCount: `${pendingCount} invoices pending`,
        totalPaid: formatCurrency(totalRevenue),
        paidCount: `${transformedTransactions.filter(t => t.status === "success").length} invoices paid`,
        avgPaidPerLead: formatCurrency(avgPaidPerLead),
        totalLeads: `${transformedTransactions.length} total leads`,
      };
      setMetricsData(metricsDataValue);


      const serviceRevenue = {};
      transformedTransactions.forEach(t => {
        const service = t.serviceType;
        if (service) {
          if (!serviceRevenue[service]) {
            serviceRevenue[service] = 0;
          }
          serviceRevenue[service] += t.paid;
        }
      });

      const serviceLabels = { skin: "Skin Care", dental: "Dental", hair: "Hair Care" };
      const totalServiceRevenue = Object.values(serviceRevenue).reduce((a, b) => a + b, 0);
      const revenueByService = Object.entries(serviceRevenue).map(([key, amount]) => ({
        name: serviceLabels[key] || key,
        value: totalServiceRevenue > 0 ? Math.round((amount / totalServiceRevenue) * 100) : 0,
        amount,
      }));

      setRevenueByServiceData(revenueByService);


      const dailyRevenue = {};
      // Only include payments within the selected date range
      filteredItems.forEach(item => {
        const relevantPayments = item.paymentsInRange && item.paymentsInRange.length > 0
          ? item.paymentsInRange
          : (dateRange?.startDate && dateRange?.endDate
              ? (item.payments || []).filter(p => {
                  const paymentDate = new Date(p.created_at);
                  const startDate = new Date(dateRange.startDate);
                  const endDate = new Date(dateRange.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  return paymentDate >= startDate && paymentDate <= endDate;
                })
              : (item.payments || []));

        relevantPayments.forEach(payment => {
          if (payment.created_at && payment.amount) {
            const dateKey = new Date(payment.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            }).toUpperCase();
            const rawDate = new Date(payment.created_at);
            if (!dailyRevenue[dateKey]) {
              dailyRevenue[dateKey] = { revenue: 0, rawDate };
            }
            dailyRevenue[dateKey].revenue += Number(payment.amount) || 0;
          }
        });
      });

      const trendData = Object.entries(dailyRevenue)
        .map(([date, data]) => ({
          date,
          revenue: data.revenue,
          rawDate: data.rawDate,
          growth: 0,
        }))
        .sort((a, b) => a.rawDate - b.rawDate)
        .slice(-10); 

     
      let cumulativeRevenue = 0;
      trendData.forEach((item, index) => {
        cumulativeRevenue += item.revenue;
        if (index > 0 && trendData[index - 1].revenue > 0) {
          item.growth = Math.round(((item.revenue - trendData[index - 1].revenue) / trendData[index - 1].revenue) * 100);
        }
      });

      setRevenueTrendData(trendData);


    } catch (err) {
      console.error("Error fetching payment data:", err);
      setError(err.message || "Failed to load payment reports");
    } finally {
      setLoading(false);
    }
  }, [dateRangeKey, filtersKey]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toLocaleString();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };


  if (loading && transactionData.length === 0) {
    return <ReportLoader />;
  }


  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 400, p: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: colors.textPrimary,
              mb: 1,
            }}
          >
            Failed to Load Reports
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: colors.textSecondary, mb: 3 }}
          >
            {error}
          </Typography>
          <Box
            component="button"
            onClick={fetchPaymentData}
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              color: "#FFF",
              bgcolor: primaryColor,
              border: "none",
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": {
                bgcolor: colors.primaryDark,
              },
            }}
          >
            Retry
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Metrics Cards */}
      <PaymentMetrics key={`metrics-${dateRangeKey}`} data={metricsData} />

      {/* Charts Row - Revenue Trend and Revenue by Service */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 3,
        }}
      >
        <RevenueTrend key={`trend-${dateRangeKey}`} data={revenueTrendData} />
        <RevenueByService key={`service-${dateRangeKey}`} data={revenueByServiceData} />
      </Box>

      {/* Transaction History Table */}
      <TransactionHistory
        key={`transactions-${dateRangeKey}`}
        data={transactionData}
        onFilter={handleFilterChange}
        onRefresh={fetchPaymentData}
        loading={loading}
      />
    </Box>
  );
};

export default Payments;
